import React, { useState } from 'react';
import { Container, Typography, Box, Paper, List, ListItem, ListItemIcon, ListItemText, Button, Divider, Grid, Card, CardContent, CardMedia } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowRight from '@mui/icons-material/ArrowRight';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import ChatIcon from '@mui/icons-material/Chat';
import DynamicInfographic from '../../../components/infographics/DynamicInfographic';
import FallRiskInfographics from '../../../components/infographics/FallRiskInfographics';
import ModuleSidebar from '../../../components/navigation/ModuleSidebar';
import { scrollToTop } from '../../../utils/scrollUtils';

const InitialAssessment = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('2.1');
  const [quizAnswers, setQuizAnswers] = useState({});
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showExplanations, setShowExplanations] = useState({});
  const [answerStatus, setAnswerStatus] = useState({});
  
  const handleBack = () => {
    navigate('/courses/choking-management');
    scrollToTop();
  };

  // Function to navigate to the next section
  const handleNextSection = () => {
    const currentSectionIndex = sections.findIndex(section => section.id === activeSection);
    if (currentSectionIndex < sections.length - 1) {
      const nextSection = sections[currentSectionIndex + 1].id;
      setActiveSection(nextSection);
      scrollToTop();
    }
  };

  // Define correct answers for each question
  const correctAnswers = {
    '2.1-1': 1, // Lock wheels on wheelchairs, beds, and commodes
    '2.1-2': 0, // Position wheelchair at 45° angle to bed
    '2.1-3': 2, // Bend knees, keep back straight during transfer
    '2.2-1': 0, // Allow extra time for completion without rushing
    '2.2-2': 2, // Break complex activities into manageable steps
    '2.2-3': 1, // Consider physical, cognitive, and emotional factors
    '2.3-1': 1, // Use leg muscles instead of back muscles
    '2.3-2': 2, // Maintain proximity to the person or object
    '2.3-3': 0, // Perform regular stretching exercises
    '2.4-1': 2, // Acknowledge fears while building confidence
    '2.4-2': 1, // Visual examples improve understanding and confidence
    '2.4-3': 0, // Creates connection and shows attentiveness
    '2.4-1': 2, // They're only administered by medical professionals with proper training
    '2.4-2': 1, // Helps determine if advanced techniques are necessary
    '2.4-3': 0, // Oral airway devices
  };

  const handleAnswerSelect = (sectionId, questionId, answerIndex) => {
    const key = `${sectionId}-${questionId}`;
    
    // Check if the correct answer has already been selected for this question
    // If so, don't allow further selections
    if (answerStatus[key] === true) {
      return;
    }
    
    // Store the selected answer
    setSelectedAnswers(prev => ({
      ...prev,
      [key]: answerIndex
    }));
    
    // Check if the selected answer is correct
    const isCorrect = answerIndex === correctAnswers[key];
    
    // Only show explanation if the correct answer is selected
    if (isCorrect) {
      setShowExplanations(prev => ({
        ...prev,
        [key]: true
      }));
    } else {
      // If incorrect, ensure explanation is hidden
      setShowExplanations(prev => ({
        ...prev,
        [key]: false
      }));
    }
    
    // Track if answer is correct or incorrect
    setAnswerStatus(prev => ({
      ...prev,
      [key]: isCorrect
    }));
  };

  // Function to handle section changes
  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    scrollToTop();
  };
  
  // Define all sections for the module
  const sections = [
    {
      id: "2.1",
      title: "2.1 Partial Airway Obstruction",
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              color: "primary.main",
              display: "flex",
              alignItems: "center",
            }}
          >
            <TransferWithinAStationIcon sx={{ mr: 1, color: "#81c784" }} />
            2.1 Partial Airway Obstruction
          </Typography>

          <Typography paragraph>
            When dealing with a patient who has a partial airway obstruction, the airway is partially blocked but the patient can still cough forcefully and breathe. The goal is to support the patient without aggressive intervention unless their condition worsens.
          </Typography>

          {/* Data Visualization for Partial Airway Obstruction */}
          <Box sx={{ mb: 4, p: 3, borderRadius: 2, bgcolor: "rgba(129, 199, 132, 0.1)", border: "1px solid rgba(129, 199, 132, 0.3)" }}>
            <Typography variant="h6" gutterBottom sx={{ color: "#2e7d32", fontWeight: 600 }}>
              Patient Outcomes Based on Initial Response Approach
            </Typography>
            
            <Box sx={{ display: "flex", flexDirection: "column", mt: 3 }}>
              {/* Bar Chart Visualization */}
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="body2" sx={{ minWidth: 220 }}>Encourage Coughing</Typography>
                <Box sx={{ height: 24, width: "92%", bgcolor: "#81c784", borderRadius: 1, ml: 2 }}></Box>
                <Typography variant="body2" sx={{ ml: 2 }}>92%</Typography>
              </Box>
              
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="body2" sx={{ minWidth: 220 }}>Monitoring + Reassurance</Typography>
                <Box sx={{ height: 24, width: "88%", bgcolor: "#81c784", borderRadius: 1, ml: 2 }}></Box>
                <Typography variant="body2" sx={{ ml: 2 }}>88%</Typography>
              </Box>
              
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="body2" sx={{ minWidth: 220 }}>Premature Back Blows</Typography>
                <Box sx={{ height: 24, width: "45%", bgcolor: "#ff9800", borderRadius: 1, ml: 2 }}></Box>
                <Typography variant="body2" sx={{ ml: 2 }}>45%</Typography>
              </Box>
              
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="body2" sx={{ minWidth: 220 }}>Immediate Abdominal Thrusts</Typography>
                <Box sx={{ height: 24, width: "30%", bgcolor: "#f44336", borderRadius: 1, ml: 2 }}></Box>
                <Typography variant="body2" sx={{ ml: 2 }}>30%</Typography>
              </Box>
              
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="body2" sx={{ minWidth: 220 }}>Finger Sweep Attempts</Typography>
                <Box sx={{ height: 24, width: "15%", bgcolor: "#f44336", borderRadius: 1, ml: 2 }}></Box>
                <Typography variant="body2" sx={{ ml: 2 }}>15%</Typography>
              </Box>
            </Box>
            
            <Typography variant="caption" sx={{ display: "block", mt:.5, textAlign: "center", fontStyle: "italic" }}>
              Percentage of successful outcomes without complication in partial airway obstruction cases
            </Typography>
          </Box>

          {/* Dynamic Infographic - Switch between pre-designed and AI-generated */}
          <DynamicInfographic
            module="CaregiverBestPractices"
            topic="Partial Airway Obstruction Response"
          />

          <Box
            sx={{
              bgcolor: "rgba(129, 199, 132, 0.1)",
              p: 2,
              borderRadius: 2,
              mb: 2,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Key Response Principles
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "white" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ color: "#81c784", fontWeight: 600 }}
                    >
                      Initial Response
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Encourage forceful coughing" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Stay calm and reassuring" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Monitor breathing and color" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "white" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ color: "#81c784", fontWeight: 600 }}
                    >
                      What to Avoid
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Premature back blows" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Abdominal thrusts unless needed" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Finger sweeps" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "white" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ color: "#81c784", fontWeight: 600 }}
                    >
                      Warning Signs
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Weakening cough" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Difficulty breathing" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText primary="Signs of cyanosis" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          {/* Quiz for Section 2.1 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "#81c784", fontWeight: 600 }}
            >
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What is the best initial response for a patient with a partial airway obstruction?
                    </Typography>
                    <List dense>
                      {[
                        "Perform abdominal thrusts immediately",
                        "Encourage the patient to continue coughing",
                        "Attempt to remove the object with a finger sweep",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["2.1-1"] === index) {
                          if (index === correctAnswers["2.1-1"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["2.1-1"] &&
                          index === correctAnswers["2.1-1"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("2.1", "1", index)
                            }
                            disabled={
                              answerStatus["2.1-1"] === true &&
                              index !== correctAnswers["2.1-1"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["2.1-1"] === true &&
                                index !== correctAnswers["2.1-1"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["2.1-1"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Forceful coughing is often enough to clear a partial obstruction without additional intervention. Premature intervention may worsen the blockage.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      When should you intervene if a patient is coughing forcefully?
                    </Typography>
                    <List dense>
                      {[
                        "As soon as you see signs of choking",
                        "Only if the patient's condition worsens or they stop coughing",
                        "Immediately perform back blows and abdominal thrusts",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["2.1-2"] === index) {
                          if (index === correctAnswers["2.1-2"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["2.1-2"] &&
                          index === correctAnswers["2.1-2"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("2.1", "2", index)
                            }
                            disabled={
                              answerStatus["2.1-2"] === true &&
                              index !== correctAnswers["2.1-2"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["2.1-2"] === true &&
                                index !== correctAnswers["2.1-2"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["2.1-2"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Premature intervention may worsen the blockage; monitoring is key. Only intervene if the patient's condition deteriorates or they stop coughing effectively.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What is a sign that a partial obstruction may be worsening?
                    </Typography>
                    <List dense>
                      {[
                        "The patient's cough becomes weaker or silent",
                        "The patient's cough becomes louder and more forceful",
                        "The patient starts sweating heavily",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["2.1-3"] === index) {
                          if (index === correctAnswers["2.1-3"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["2.1-3"] &&
                          index === correctAnswers["2.1-3"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("2.1", "3", index)
                            }
                            disabled={
                              answerStatus["2.1-3"] === true &&
                              index !== correctAnswers["2.1-3"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["2.1-3"] === true &&
                                index !== correctAnswers["2.1-3"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["2.1-3"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          A weak or absent cough suggests that the obstruction may be progressing to a complete blockage, requiring immediate intervention.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      ),
    },
    {
      id: "2.2",
      title: "2.2 Complete Airway Obstruction",
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              color: "primary.main",
              display: "flex",
              alignItems: "center",
            }}
          >
            <AccessibilityNewIcon sx={{ mr: 1, color: "#81c784" }} /> 2.2
            Complete Airway Obstruction
          </Typography>

          <Typography paragraph>
            When a patient is unable to speak, breathe, or cough, their airway is completely blocked. Immediate intervention is required to prevent respiratory failure and potential death.
          </Typography>

          

          <Box
            sx={{
              mb: 3,
              p: 3,
              borderRadius: 2,
              bgcolor: "rgba(129, 199, 132, 0.1)",
              border: "1px solid rgba(129, 199, 132, 0.3)",
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                color: "#2e7d32",
                fontWeight: 600,
                borderBottom: "1px solid rgba(129, 199, 132, 0.5)",
                pb: 1,
              }}
            >
              Emergency Response Protocol
            </Typography>

            <Typography paragraph sx={{ mt: 2 }}>
              A complete airway obstruction requires immediate action. The Heimlich maneuver (abdominal thrusts) is the primary intervention for conscious patients, while chest thrusts are used for pregnant or obese patients.
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "rgba(129, 199, 132, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#2e7d32", fontWeight: 600 }}
                    >
                      1
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}
                    >
                      Call for Help
                    </Typography>
                    <Typography variant="body2">
                      Activate emergency services immediately. Quick response is crucial to ensure medical backup arrives on time.
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "rgba(129, 199, 132, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#2e7d32", fontWeight: 600 }}
                    >
                      2
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}
                    >
                      Assess Patient Position
                    </Typography>
                    <Typography variant="body2">
                      Determine if the patient is pregnant or obese to choose between abdominal or chest thrusts.
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "rgba(129, 199, 132, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#2e7d32", fontWeight: 600 }}
                    >
                      3
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}
                    >
                      Perform Appropriate Thrusts
                    </Typography>
                    <Typography variant="body2">
                      For standard patients: Use abdominal thrusts (Heimlich maneuver). For pregnant/obese patients: Use chest thrusts.
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "rgba(129, 199, 132, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#2e7d32", fontWeight: 600 }}
                    >
                      4
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}
                    >
                      Monitor Response
                    </Typography>
                    <Typography variant="body2">
                      Continue thrusts until the object is expelled or the patient becomes unconscious.
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card
                variant="outlined"
                sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "#81c784", fontWeight: 600 }}
                  >
                    Abdominal Thrusts (Heimlich Maneuver)
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Stand behind the patient"
                        secondary="Wrap arms around their waist"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Form a fist"
                        secondary="Place just above the navel"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Grasp fist with other hand"
                        secondary="Deliver quick, upward thrusts"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Repeat until object is expelled"
                        secondary="Or until patient becomes unconscious"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                variant="outlined"
                sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "#81c784", fontWeight: 600 }}
                  >
                    Chest Thrusts (Modified Technique)
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Stand behind the patient"
                        secondary="Place hands on lower sternum"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Deliver inward thrusts"
                        secondary="With controlled force"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Continue until obstruction is relieved"
                        secondary="Or until patient becomes unconscious"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Quiz for Section 2.2 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "#81c784", fontWeight: 600 }}
            >
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What is the correct depth for chest compressions in an unconscious adult choking patient?
                    </Typography>
                    <List dense>
                      {[
                        "About 1 inch deep",
                        "About 2 inches deep",
                        "As deep as possible",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["2.2-1"] === index) {
                          if (index === correctAnswers["2.2-1"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["2.2-1"] &&
                          index === correctAnswers["2.2-1"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("2.2", "1", index)
                            }
                            disabled={
                              answerStatus["2.2-1"] === true &&
                              index !== correctAnswers["2.2-1"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["2.2-1"] === true &&
                                index !== correctAnswers["2.2-1"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["2.2-1"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Effective chest compressions at 2 inches depth generate enough pressure to circulate blood and potentially dislodge an obstruction.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What should you do if a pregnant patient is choking and unable to cough or breathe?
                    </Typography>
                    <List dense>
                      {[
                        "Perform abdominal thrusts as usual",
                        "Use back blows instead of thrusts",
                        "Perform chest thrusts by placing hands on the lower sternum",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["2.2-2"] === index) {
                          if (index === correctAnswers["2.2-2"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["2.2-2"] &&
                          index === correctAnswers["2.2-2"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("2.2", "2", index)
                            }
                            disabled={
                              answerStatus["2.2-2"] === true &&
                              index !== correctAnswers["2.2-2"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["2.2-2"] === true &&
                                index !== correctAnswers["2.2-2"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["2.2-2"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Chest thrusts reduce pressure on the abdomen, ensuring safety for the unborn child while still effectively dislodging the obstruction.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      If a choking patient loses consciousness, what should you do first?
                    </Typography>
                    <List dense>
                      {[
                        "Start rescue breaths",
                        "Call for emergency help and start CPR",
                        "Perform finger sweeps to remove the object",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["2.2-3"] === index) {
                          if (index === correctAnswers["2.2-3"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["2.2-3"] &&
                          index === correctAnswers["2.2-3"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("2.2", "3", index)
                            }
                            disabled={
                              answerStatus["2.2-3"] === true &&
                              index !== correctAnswers["2.2-3"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["2.2-3"] === true &&
                                index !== correctAnswers["2.2-3"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["2.2-3"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          In an unconscious choking patient, CPR can help dislodge the object while maintaining circulation. Emergency help should be called immediately.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      ),
    },
    {
      id: "2.3",
      title: "2.3 Unconscious Patient",
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              color: "primary.main",
              display: "flex",
              alignItems: "center",
            }}
          >
            <AccessibilityNewIcon sx={{ mr: 1, color: "#81c784" }} /> 2.3
            Unconscious Patient
          </Typography>

          <Typography paragraph>
            When a patient becomes unresponsive during a choking emergency, immediate CPR is required. This section outlines the proper sequence of actions for managing an unconscious choking patient.
          </Typography>

          {/* Data Visualization for Unconscious Patient */}
          <Box sx={{ mb: 4, p: 3, borderRadius: 2, bgcolor: "rgba(129, 199, 132, 0.1)", border: "1px solid rgba(129, 199, 132, 0.3)" }}>
            <Typography variant="h6" gutterBottom sx={{ color: "#2e7d32", fontWeight: 600 }}>
              CPR Effectiveness in Choking-Induced Cardiac Arrest
            </Typography>
            
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Pie Chart 1: Survival Rates by CPR Initiation Time */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" align="center" gutterBottom sx={{ color: "#2e7d32", fontWeight: 600 }}>
                  Survival Rates by CPR Initiation Time
                </Typography>
                
                <Box sx={{ display: "flex", justifyContent: "center", position: "relative", height: 200 }}>
                  {/* Base circle (entire population) */}
                  <Box sx={{ 
                    width: 200, 
                    height: 200, 
                    borderRadius: "50%", 
                    position: "relative",
                    overflow: "hidden"
                  }}>
                    {/* Segment 1: Within 1 minute */}
                    <Box sx={{ 
                      position: "absolute", 
                      top: 0, 
                      left: 0, 
                      width: "100%", 
                      height: "100%", 
                      background: "#4caf50",
                      clipPath: "polygon(50% 50%, 50% 0%, 100% 0%, 100% 70%, 50% 50%)"
                    }}></Box>
                    
                    {/* Segment 2: 1-3 minutes */}
                    <Box sx={{ 
                      position: "absolute", 
                      top: 0, 
                      left: 0, 
                      width: "100%", 
                      height: "100%", 
                      background: "#8bc34a",
                      clipPath: "polygon(50% 50%, 100% 70%, 100% 100%, 70% 100%, 50% 50%)"
                    }}></Box>
                    
                    {/* Segment 3: 3-5 minutes */}
                    <Box sx={{ 
                      position: "absolute", 
                      top: 0, 
                      left: 0, 
                      width: "100%", 
                      height: "100%", 
                      background: "#ffeb3b",
                      clipPath: "polygon(50% 50%, 70% 100%, 30% 100%, 0% 80%, 50% 50%)"
                    }}></Box>
                    
                    {/* Segment 4: 5+ minutes */}
                    <Box sx={{ 
                      position: "absolute", 
                      top: 0, 
                      left: 0, 
                      width: "100%", 
                      height: "100%", 
                      background: "#f44336",
                      clipPath: "polygon(50% 50%, 0% 80%, 0% 0%, 50% 0%, 50% 50%)"
                    }}></Box>
                    
                    {/* Center circle */}
                    <Box sx={{ 
                      position: "absolute", 
                      top: "50%", 
                      left: "50%", 
                      width: 70, 
                      height: 70, 
                      borderRadius: "50%", 
                      background: "white",
                      transform: "translate(-50%, -50%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "bold"
                    }}>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        CPR<br/>Timing
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                
                {/* Legend */}
                <Box sx={{ display: "flex", flexDirection: "column", mt: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: "#4caf50", mr: 1 }}></Box>
                    <Typography variant="caption">Within 1 min: 70% survival</Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: "#8bc34a", mr: 1 }}></Box>
                    <Typography variant="caption">1-3 min: 50% survival</Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: "#ffeb3b", mr: 1 }}></Box>
                    <Typography variant="caption">3-5 min: 25% survival</Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: "#f44336", mr: 1 }}></Box>
                    <Typography variant="caption">5+ min: &lt;10% survival</Typography>
                  </Box>
                </Box>
              </Grid>
              
              {/* Bar Chart: Compression Quality Impact */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" align="center" gutterBottom sx={{ color: "#2e7d32", fontWeight: 600 }}>
                  Impact of Compression Quality on Survival
                </Typography>
                
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Typography variant="caption" sx={{ minWidth: 100 }}>Proper Rate</Typography>
                    <Box sx={{ height: 24, flexGrow: 1, position: "relative" }}>
                      <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, bgcolor: "#e0e0e0", borderRadius: 1 }}></Box>
                      <Box sx={{ position: "absolute", top: 0, left: 0, width: "85%", bottom: 0, bgcolor: "#4caf50", borderRadius: 1 }}></Box>
                    </Box>
                    <Typography variant="caption" sx={{ ml: 1, minWidth: 30 }}>85%</Typography>
                  </Box>
                  
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Typography variant="caption" sx={{ minWidth: 100 }}>Proper Depth</Typography>
                    <Box sx={{ height: 24, flexGrow: 1, position: "relative" }}>
                      <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, bgcolor: "#e0e0e0", borderRadius: 1 }}></Box>
                      <Box sx={{ position: "absolute", top: 0, left: 0, width: "78%", bottom: 0, bgcolor: "#4caf50", borderRadius: 1 }}></Box>
                    </Box>
                    <Typography variant="caption" sx={{ ml: 1, minWidth: 30 }}>78%</Typography>
                  </Box>
                  
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Typography variant="caption" sx={{ minWidth: 100 }}>Full Recoil</Typography>
                    <Box sx={{ height: 24, flexGrow: 1, position: "relative" }}>
                      <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, bgcolor: "#e0e0e0", borderRadius: 1 }}></Box>
                      <Box sx={{ position: "absolute", top: 0, left: 0, width: "70%", bottom: 0, bgcolor: "#4caf50", borderRadius: 1 }}></Box>
                    </Box>
                    <Typography variant="caption" sx={{ ml: 1, minWidth: 30 }}>70%</Typography>
                  </Box>
                  
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography variant="caption" sx={{ minWidth: 100 }}>Minimal Interruptions</Typography>
                    <Box sx={{ height: 24, flexGrow: 1, position: "relative" }}>
                      <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, bgcolor: "#e0e0e0", borderRadius: 1 }}></Box>
                      <Box sx={{ position: "absolute", top: 0, left: 0, width: "90%", bottom: 0, bgcolor: "#4caf50", borderRadius: 1 }}></Box>
                    </Box>
                    <Typography variant="caption" sx={{ ml: 1, minWidth: 30 }}>90%</Typography>
                  </Box>
                </Box>
                
                <Typography variant="caption" align="center" sx={{ display: "block", mt: 1, fontStyle: "italic" }}>
                  Percentage of successful resuscitations when each factor is performed correctly
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Box
            sx={{
              mb: 3,
              p: 3,
              borderRadius: 2,
              bgcolor: "rgba(129, 199, 132, 0.1)",
              border: "1px solid rgba(129, 199, 132, 0.3)",
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                color: "#2e7d32",
                fontWeight: 600,
                borderBottom: "1px solid rgba(129, 199, 132, 0.5)",
                pb: 1,
              }}
            >
              CPR Protocol for Unconscious Choking Patient
            </Typography>

            <Typography paragraph sx={{ mt: 2 }}>
              The sequence for managing an unconscious choking patient follows the ABC (Airway, Breathing, Circulation) approach, with specific modifications for the choking scenario.
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "rgba(129, 199, 132, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#2e7d32", fontWeight: 600 }}
                    >
                      1
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}
                    >
                      Assess Responsiveness
                    </Typography>
                    <Typography variant="body2">
                      Check if the patient is unresponsive and not breathing normally.
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "rgba(129, 199, 132, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#2e7d32", fontWeight: 600 }}
                    >
                      2
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}
                    >
                      Begin Chest Compressions
                    </Typography>
                    <Typography variant="body2">
                      Start with 30 chest compressions at 100-120 compressions per minute.
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "rgba(129, 199, 132, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#2e7d32", fontWeight: 600 }}
                    >
                      3
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}
                    >
                      Check Airway
                    </Typography>
                    <Typography variant="body2">
                      After compressions, check for visible objects and attempt rescue breaths.
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "rgba(129, 199, 132, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#2e7d32", fontWeight: 600 }}
                    >
                      4
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}
                    >
                      Continue CPR
                    </Typography>
                    <Typography variant="body2">
                      Alternate between 30 compressions and 2 breaths until help arrives.
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card
                variant="outlined"
                sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "#81c784", fontWeight: 600 }}
                  >
                    Chest Compressions
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Rate"
                        secondary="100-120 compressions per minute"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Depth"
                        secondary="At least 2 inches for adults"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Technique"
                        secondary="Push hard and fast, allowing full chest recoil"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                variant="outlined"
                sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "#81c784", fontWeight: 600 }}
                  >
                    Airway Management
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Head Tilt-Chin Lift"
                        secondary="Open airway after compressions"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Visual Inspection"
                        secondary="Look for visible objects"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Finger Sweep"
                        secondary="Only if object is clearly visible"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Quiz for Section 2.3 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "#81c784", fontWeight: 600 }}
            >
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What is the correct depth for chest compressions in an unconscious adult choking patient?
                    </Typography>
                    <List dense>
                      {[
                        "About 1 inch deep",
                        "About 2 inches deep",
                        "As deep as possible",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["2.3-1"] === index) {
                          if (index === correctAnswers["2.3-1"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["2.3-1"] &&
                          index === correctAnswers["2.3-1"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("2.3", "1", index)
                            }
                            disabled={
                              answerStatus["2.3-1"] === true &&
                              index !== correctAnswers["2.3-1"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["2.3-1"] === true &&
                                index !== correctAnswers["2.3-1"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["2.3-1"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Effective chest compressions at 2 inches depth generate enough pressure to circulate blood and potentially dislodge an obstruction.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      When should you perform a finger sweep in an unconscious patient?
                    </Typography>
                    <List dense>
                      {[
                        "Always, even if you can't see the object",
                        "Only if you can clearly see and grasp the object",
                        "Only after performing two sets of chest compressions",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["2.3-2"] === index) {
                          if (index === correctAnswers["2.3-2"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["2.3-2"] &&
                          index === correctAnswers["2.3-2"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("2.3", "2", index)
                            }
                            disabled={
                              answerStatus["2.3-2"] === true &&
                              index !== correctAnswers["2.3-2"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["2.3-2"] === true &&
                                index !== correctAnswers["2.3-2"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["2.3-2"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Blind finger sweeps risk pushing the object deeper into the airway. Only perform a finger sweep if you can clearly see and grasp the object.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What is the recommended chest compression rate during CPR?
                    </Typography>
                    <List dense>
                      {[
                        "60-80 compressions per minute",
                        "100-120 compressions per minute",
                        "As fast as possible",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["2.3-3"] === index) {
                          if (index === correctAnswers["2.3-3"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["2.3-3"] &&
                          index === correctAnswers["2.3-3"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("2.3", "3", index)
                            }
                            disabled={
                              answerStatus["2.3-3"] === true &&
                              index !== correctAnswers["2.3-3"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["2.3-3"] === true &&
                                index !== correctAnswers["2.3-3"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["2.3-3"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          A rate of 100-120 compressions per minute maximizes circulation while ensuring effective compression depth and recoil.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      ),
    },
    {
      id: "2.4",
      title: "2.4 Advanced Airway Management Techniques",
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              color: "primary.main",
              display: "flex",
              alignItems: "center",
            }}
          >
            <FitnessCenterIcon sx={{ mr: 1, color: "#81c784" }} />
            2.4 Advanced Airway Management Techniques
          </Typography>

          <Typography paragraph>
            While basic choking interventions are often sufficient, caregivers should be aware of advanced airway management techniques that may be employed by healthcare professionals. Understanding these techniques can help caregivers better communicate with medical staff during choking emergencies.
          </Typography>

          {/* Data Visualization for Advanced Airway Management */}
          <Box sx={{ mb: 4, p: 3, borderRadius: 2, bgcolor: "rgba(129, 199, 132, 0.1)", border: "1px solid rgba(129, 199, 132, 0.3)" }}>
            <Typography variant="h6" gutterBottom sx={{ color: "#2e7d32", fontWeight: 600 }}>
              Success Rates of Advanced Airway Techniques by Provider Type
            </Typography>
            
            <Box sx={{ mt: 3, position: "relative" }}>
              {/* Heat map visualization */}
              <Grid container spacing={1}>
                <Grid item xs={3}></Grid>
                <Grid item xs={3}>
                  <Typography variant="caption" align="center" sx={{ display: "block", fontWeight: 600 }}>
                    Paramedics
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="caption" align="center" sx={{ display: "block", fontWeight: 600 }}>
                    Emergency Physicians
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="caption" align="center" sx={{ display: "block", fontWeight: 600 }}>
                    Anesthesiologists
                  </Typography>
                </Grid>
                
                {/* Oral Airways */}
                <Grid item xs={3}>
                  <Typography variant="caption" sx={{ display: "block", textAlign: "right", pr: 2 }}>
                    Oral Airways
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ p: 1, bgcolor: "#c8e6c9", borderRadius: 1, textAlign: "center" }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>92%</Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ p: 1, bgcolor: "#a5d6a7", borderRadius: 1, textAlign: "center" }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>97%</Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ p: 1, bgcolor: "#81c784", borderRadius: 1, textAlign: "center" }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>99%</Typography>
                  </Box>
                </Grid>
                
                {/* Nasal Airways */}
                <Grid item xs={3}>
                  <Typography variant="caption" sx={{ display: "block", textAlign: "right", pr: 2 }}>
                    Nasal Airways
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ p: 1, bgcolor: "#e8f5e9", borderRadius: 1, textAlign: "center" }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>85%</Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ p: 1, bgcolor: "#c8e6c9", borderRadius: 1, textAlign: "center" }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>92%</Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ p: 1, bgcolor: "#a5d6a7", borderRadius: 1, textAlign: "center" }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>96%</Typography>
                  </Box>
                </Grid>
                
                {/* Bag-Valve-Mask */}
                <Grid item xs={3}>
                  <Typography variant="caption" sx={{ display: "block", textAlign: "right", pr: 2 }}>
                    Bag-Valve-Mask
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ p: 1, bgcolor: "#e8f5e9", borderRadius: 1, textAlign: "center" }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>80%</Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ p: 1, bgcolor: "#c8e6c9", borderRadius: 1, textAlign: "center" }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>90%</Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ p: 1, bgcolor: "#a5d6a7", borderRadius: 1, textAlign: "center" }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>95%</Typography>
                  </Box>
                </Grid>
                
                {/* Endotracheal Intubation */}
                <Grid item xs={3}>
                  <Typography variant="caption" sx={{ display: "block", textAlign: "right", pr: 2 }}>
                    Endotracheal Intubation
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ p: 1, bgcolor: "#ffcdd2", borderRadius: 1, textAlign: "center" }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>65%</Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ p: 1, bgcolor: "#e8f5e9", borderRadius: 1, textAlign: "center" }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>85%</Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ p: 1, bgcolor: "#81c784", borderRadius: 1, textAlign: "center" }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>98%</Typography>
                  </Box>
                </Grid>
                
                {/* Surgical Airway */}
                <Grid item xs={3}>
                  <Typography variant="caption" sx={{ display: "block", textAlign: "right", pr: 2 }}>
                    Surgical Airway
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ p: 1, bgcolor: "#ef9a9a", borderRadius: 1, textAlign: "center" }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>30%</Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ p: 1, bgcolor: "#ffcdd2", borderRadius: 1, textAlign: "center" }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>70%</Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ p: 1, bgcolor: "#c8e6c9", borderRadius: 1, textAlign: "center" }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>92%</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
            
            <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", mx: 1 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: "#ef9a9a", mr: 1 }}></Box>
                <Typography variant="caption">Low Success (&lt;50%)</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mx: 1 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: "#ffcdd2", mr: 1 }}></Box>
                <Typography variant="caption">Moderate (50-75%)</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mx: 1 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: "#e8f5e9", mr: 1 }}></Box>
                <Typography variant="caption">Good (75-90%)</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mx: 1 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: "#c8e6c9", mr: 1 }}></Box>
                <Typography variant="caption">Very Good (90-95%)</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mx: 1 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: "#a5d6a7", mr: 1 }}></Box>
                <Typography variant="caption">Excellent (95-98%)</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mx: 1 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: "#81c784", mr: 1 }}></Box>
                <Typography variant="caption">Superior (&gt;98%)</Typography>
              </Box>
            </Box>
            
            <Typography variant="caption" sx={{ display: "block", mt: 2, textAlign: "center", fontStyle: "italic" }}>
              Success rates based on first-attempt success in emergency settings. Training level and experience significantly impact outcomes.
            </Typography>
          </Box>

          <Box
            sx={{
              my: 3,
              p: 3,
              borderRadius: 2,
              bgcolor: "rgba(129, 199, 132, 0.1)",
              border: "1px solid rgba(129, 199, 132, 0.3)",
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                color: "#2e7d32",
                fontWeight: 600,
                borderBottom: "1px solid rgba(129, 199, 132, 0.5)",
                pb: 1,
              }}
            >
              Advanced Airway Management Overview
            </Typography>

            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Visualization - Bar chart showing utilization of different techniques */}
              <Grid item xs={12}>
                <Box sx={{ width: "100%", height: 300, bgcolor: "white", p: 2, borderRadius: 1, border: "1px solid rgba(0,0,0,0.1)" }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Frequency of Advanced Techniques in Choking Emergencies
                  </Typography>
                  <Box sx={{ display: "flex", height: "85%", alignItems: "flex-end", justifyContent: "space-around", pt: 2 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", height: "100%" }}>
                      <Box sx={{ width: 80, height: "40%", bgcolor: "#81c784", borderTopLeftRadius: 4, borderTopRightRadius: 4 }}></Box>
                      <Typography variant="body2" sx={{ mt: 1 }}>Oral Airways</Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>40%</Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", height: "100%" }}>
                      <Box sx={{ width: 80, height: "25%", bgcolor: "#81c784", borderTopLeftRadius: 4, borderTopRightRadius: 4 }}></Box>
                      <Typography variant="body2" sx={{ mt: 1 }}>Nasal Airways</Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>25%</Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", height: "100%" }}>
                      <Box sx={{ width: 80, height: "20%", bgcolor: "#81c784", borderTopLeftRadius: 4, borderTopRightRadius: 4 }}></Box>
                      <Typography variant="body2" sx={{ mt: 1 }}>Laryngeal Mask</Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>20%</Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", height: "100%" }}>
                      <Box sx={{ width: 80, height: "10%", bgcolor: "#81c784", borderTopLeftRadius: 4, borderTopRightRadius: 4 }}></Box>
                      <Typography variant="body2" sx={{ mt: 1 }}>Intubation</Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>10%</Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", height: "100%" }}>
                      <Box sx={{ width: 80, height: "5%", bgcolor: "#81c784", borderTopLeftRadius: 4, borderTopRightRadius: 4 }}></Box>
                      <Typography variant="body2" sx={{ mt: 1 }}>Cricothyrotomy</Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>5%</Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card
                variant="outlined"
                sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "#81c784", fontWeight: 600 }}
                  >
                    Basic Airway Adjuncts
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Oropharyngeal Airways"
                        secondary="Curved plastic devices inserted into the mouth to prevent the tongue from blocking the airway"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Nasopharyngeal Airways"
                        secondary="Soft rubber tubes inserted through the nose to establish an airway"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Supplemental Oxygen"
                        secondary="Used after the obstruction is cleared to improve oxygenation"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                variant="outlined"
                sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "#81c784", fontWeight: 600 }}
                  >
                    Advanced Interventions
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Laryngeal Mask Airways (LMA)"
                        secondary="A device placed in the throat to create a seal around the larynx"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Endotracheal Intubation"
                        secondary="Insertion of a flexible tube through the mouth into the trachea"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Surgical Airway (Cricothyrotomy)"
                        secondary="Emergency procedure creating an incision in the throat to establish an airway"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ my: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: "#2e7d32", fontWeight: 600 }}>
              Caregiver's Role During Advanced Interventions
            </Typography>
            <Typography paragraph>
              While caregivers don't perform these advanced techniques, understanding your role during these procedures is important:
            </Typography>
            
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, borderRadius: 2, bgcolor: "rgba(129, 199, 132, 0.1)" }}>
                <Box sx={{ 
                  minWidth: 40, 
                  height: 40, 
                  borderRadius: "50%", 
                  bgcolor: "#81c784", 
                  color: "white", 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center",
                  fontWeight: "bold"
                }}>
                  1
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#2e7d32" }}>Provide Clear Patient History</Typography>
                  <Typography variant="body2">Share relevant medical history, allergies, and details about the choking incident when medical professionals arrive.</Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, borderRadius: 2, bgcolor: "rgba(129, 199, 132, 0.1)" }}>
                <Box sx={{ 
                  minWidth: 40, 
                  height: 40, 
                  borderRadius: "50%", 
                  bgcolor: "#81c784", 
                  color: "white", 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center",
                  fontWeight: "bold"
                }}>
                  2
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#2e7d32" }}>Assist as Directed</Typography>
                  <Typography variant="body2">Follow instructions from medical professionals, which may include helping position the patient or retrieving supplies.</Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, borderRadius: 2, bgcolor: "rgba(129, 199, 132, 0.1)" }}>
                <Box sx={{ 
                  minWidth: 40, 
                  height: 40, 
                  borderRadius: "50%", 
                  bgcolor: "#81c784", 
                  color: "white", 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center",
                  fontWeight: "bold"
                }}>
                  3
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#2e7d32" }}>Provide Emotional Support</Typography>
                  <Typography variant="body2">Offer reassurance to the patient and family members during medical interventions.</Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Quiz for Section 2.4 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "#81c784", fontWeight: 600 }}
            >
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Why are advanced airway management techniques not typically performed by caregivers?
                    </Typography>
                    <List dense>
                      {[
                        "They take too much time to implement",
                        "They require expensive equipment",
                        "They're only administered by medical professionals with proper training",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["2.4-1"] === index) {
                          if (index === correctAnswers["2.4-1"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["2.4-1"] &&
                          index === correctAnswers["2.4-1"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("2.4", "1", index)
                            }
                            disabled={
                              answerStatus["2.4-1"] === true &&
                              index !== correctAnswers["2.4-1"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["2.4-1"] === true &&
                                index !== correctAnswers["2.4-1"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["2.4-1"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          These techniques require specialized medical training, knowledge of anatomy, and skills that are beyond the scope of typical caregiver responsibilities.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Why is it important for caregivers to provide a clear patient history to medical professionals?
                    </Typography>
                    <List dense>
                      {[
                        "To avoid legal liability for the choking incident",
                        "Helps determine if advanced techniques are necessary",
                        "Only to fulfill documentation requirements",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["2.4-2"] === index) {
                          if (index === correctAnswers["2.4-2"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["2.4-2"] &&
                          index === correctAnswers["2.4-2"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("2.4", "2", index)
                            }
                            disabled={
                              answerStatus["2.4-2"] === true &&
                              index !== correctAnswers["2.4-2"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["2.4-2"] === true &&
                                index !== correctAnswers["2.4-2"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["2.4-2"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Patient history, including allergies, medical conditions, and details about the choking incident, helps medical professionals make informed decisions about appropriate interventions.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which of the following is considered a basic airway adjunct?
                    </Typography>
                    <List dense>
                      {[
                        "Oral airway devices",
                        "Endotracheal tubes",
                        "Surgical cricothyrotomy",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["2.4-3"] === index) {
                          if (index === correctAnswers["2.4-3"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["2.4-3"] &&
                          index === correctAnswers["2.4-3"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("2.4", "3", index)
                            }
                            disabled={
                              answerStatus["2.4-3"] === true &&
                              index !== correctAnswers["2.4-3"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["2.4-3"] === true &&
                                index !== correctAnswers["2.4-3"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["2.4-3"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Oral airway devices (oropharyngeal airways) are considered basic airway adjuncts that help maintain an open airway by preventing the tongue from blocking the airway.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      ),
    },
  ];
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        sx={{ mb: 3 }}
      >
        Back to Learning Modules
      </Button>

      <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 2 }}>
        
        <Typography
          variant="h4"
          gutterBottom
          sx={{ color: "#81c784", fontWeight: 600 }}
        >
          Module 2: Immediate Response
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 3, color: "text.secondary" }}>
          Understand the appropriate interventions for partial and complete
          airway obstructions, including the Heimlich maneuver, chest thrusts,
          and CPR
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <ModuleSidebar
              sections={sections}
              activeSection={activeSection}
              onSectionChange={handleSectionChange}
              moduleTitle="Caregiver Best Practices"
            />
          </Grid>
          <Grid item xs={12} md={9}>
            {sections.find((section) => section.id === activeSection)?.content}

            {/* Module Buttons */}
            <Box
              sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}
            >
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
              >
                Back to Learning Modules
              </Button>
              {activeSection !== sections[sections.length - 1].id && (
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={handleNextSection}
                  sx={{ bgcolor: "#81c784", "&:hover": { bgcolor: "#4caf50" } }}
                >
                  Next Section
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default InitialAssessment;
